import { useNetlifySDK } from "./use_netlify_sdk.js";

export type Fetch = (
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
) => Promise<Response>;

type CreateNetlifyExtensionUIFetchOptions = {
  fetch?: Fetch | undefined;

  configurationId?: string | null | undefined;
  dataLayerId?: string | null | undefined;
  extension: {
    id: string;
    slug: string;
  };
  netlifyToken?: string | null | undefined;
  providerToken?: string | null | undefined;
  providerTokens?:
    | {
        externalId: string;
        token: string;
        connectionId: string;
      }[]
    | undefined;
  siteId?: string | null | undefined;
  teamId?: string | null | undefined;
  user: {
    id: string | null;
  };
};

const createNetlifyExtensionUIFetch = ({
  fetch = globalThis.fetch,
  ...options
}: CreateNetlifyExtensionUIFetchOptions): Fetch => {
  return (input, init) => {
    const headers = new Headers(init?.headers);

    headers.set("Nf-UIExt-Extension-Id", options.extension.id);
    headers.set("Nf-UIExt-Extension-Slug", options.extension.slug);

    if (options.netlifyToken != null && options.netlifyToken !== "") {
      headers.set("Nf-UIExt-Netlify-Token", options.netlifyToken);
    }
    if (options.providerToken != null && options.providerToken !== "") {
      headers.set("Nf-UIExt-Provider-Token", options.providerToken);
    }
    headers.set(
      "Nf-UIExt-Provider-Tokens",
      JSON.stringify(options.providerTokens ?? []),
    );

    if (options.configurationId != null && options.configurationId !== "") {
      headers.set("Nf-UIExt-Configuration-Id", options.configurationId);
    }

    if (options.dataLayerId != null && options.dataLayerId !== "") {
      headers.set("Nf-UIExt-Datalayer-Id", options.dataLayerId);
    }

    if (options.siteId != null && options.siteId !== "") {
      headers.set("Nf-UIExt-Site-Id", options.siteId);
    }

    if (options.teamId != null && options.teamId !== "") {
      headers.set("Nf-UIExt-Team-Id", options.teamId);
    }

    if (options.user.id != null && options.user.id !== "") {
      headers.set("Nf-UIExt-User-Id", options.user.id);
    }

    return fetch(input, {
      ...init,
      headers,
    });
  };
};

/**
 * `useNetlifyExtensionUIFetch` returns a `fetch`-compatible function that automatically attaches
 * authentication data to requests. Use it to perform requests to [endpoints](/sdk/endpoints/overview/).
 *
 * @returns A fetch function.
 * @example
 * ```tsx
 * import { useNetlifyExtensionUIFetch } from "@netlify/sdk/react";
 * import { useEffect, useState } from "react";
 *
 * const MyComponent = () => {
 *   const fetch = useNetlifyExtensionUIFetch();
 *   const [data, setData] = useState(null);
 *
 *   useEffect(() => {
 *     (async () => {
 *       const res = await fetch("/.netlify/functions/my-endpoint");
 *       if (res.ok) {
 *         setData(await res.json());
 *       }
 *     })();
 *   }, [fetch, setData]);
 *
 *   if (data === null) {
 *     return <div>No data found.</div>;
 *   }
 * };
 * ```
 */
export const useNetlifyExtensionUIFetch = (): Fetch => {
  const sdk = useNetlifySDK();

  return (input, init) => {
    // Create a fresh fetch function each time we invoke fetch to make sure we have the latest
    // values from the SDK context
    const fetch = createNetlifyExtensionUIFetch({
      configurationId: sdk.context.configurationId,
      dataLayerId: sdk.context.dataLayerId,
      extension: sdk.extension,
      netlifyToken: sdk.context.auth.netlifyToken,
      providerToken: sdk.context.auth.providerToken,
      providerTokens: sdk.context.auth.providerTokens,
      siteId: sdk.context.siteId,
      teamId: sdk.context.teamId,
      user: sdk.context.user,
    });
    return fetch(input, init);
  };
};
